import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Btn, Input, Select, Card, Spinner, useToast } from '../components/shared/UI';
import codes from '../data/einvoiceCodes.json';
import { useT } from '../i18n';
import api from '../utils/api';

export const ID_TYPES = ['BRN', 'NRIC', 'PASSPORT', 'ARMY'];

function Section({ title, children }) {
  return (
    <Card style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--heading)', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border-light)' }}>{title}</h2>
      {children}
    </Card>
  );
}

export default function EInvoiceSettings() {
  const navigate = useNavigate();
  const { t } = useT();
  const { show, ToastContainer } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, lines: [] }
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/einvoice/settings').then(r => setForm({ ...r.data, clientSecret: '' })).catch(() => setForm({ enabled: false, environment: 'sandbox', idType: 'BRN', classificationCode: '022', taxType: '', clientSecret: '' }));
  }, []);

  if (!form) return <Spinner />;

  const set = k => e => { setDirty(true); setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })); };

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put('/einvoice/settings', form);
      setForm({ ...data, clientSecret: '' });
      setDirty(false);
      show(t('einv.saved'));
    } catch (err) { show(err.response?.data?.message || t('settings.errorSaving'), 'error'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    if (dirty) { show(t('einv.saveFirst'), 'warning'); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post('/einvoice/settings/test');
      const env = t(`einv.${data.environment}`);
      setTestResult({
        ok: data.tin !== 'invalid',
        lines: [t('einv.testOk', { env }), data.tin === 'valid' ? t('einv.testTinValid') : data.tin === 'invalid' ? t('einv.testTinInvalid') : null].filter(Boolean),
      });
    } catch (err) {
      const d = err.response?.data;
      setTestResult({ ok: false, lines: [d?.message || t('einv.failed'), ...(Array.isArray(d?.details) ? d.details : d?.details ? [String(d.details)] : [])] });
    } finally { setTesting(false); }
  };

  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14 };

  return (
    <form onSubmit={save}>
      <ToastContainer />
      <button type="button" onClick={() => navigate('/settings')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, marginBottom: 8, fontFamily: 'inherit', padding: 0 }}>
        ← {t('einv.back')}
      </button>
      <PageHeader title={t('einv.title')} subtitle={t('einv.subtitle')}
        actions={<Btn type="submit" variant="primary" disabled={saving}>{saving ? t('common.saving') : t('einv.save')}</Btn>} />

      <div style={{ maxWidth: 760 }}>
        <Section title={t('einv.stepsTitle')}>
          <ol style={{ margin: 0, paddingInlineStart: 20, fontSize: 13.5, color: 'var(--text)', lineHeight: 1.9 }}>
            {[1, 2, 3, 4].map(n => <li key={n}>{t(`einv.step${n}`)}</li>)}
          </ol>
          <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 12, lineHeight: 1.6 }}>{t('einv.versionNote')}</p>
        </Section>

        <Section title={t('einv.connection')}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: 'var(--text)', cursor: 'pointer', marginBottom: 16 }}>
            <input type="checkbox" checked={!!form.enabled} onChange={set('enabled')} /> {t('einv.enable')}
          </label>
          <div style={grid}>
            <Select label={t('einv.environment')} value={form.environment} onChange={set('environment')}>
              <option value="sandbox">{t('einv.sandbox')}</option>
              <option value="production">{t('einv.production')}</option>
            </Select>
            <Input label={t('einv.clientId')} value={form.clientId || ''} onChange={set('clientId')} autoComplete="off" dir="ltr" />
            <div style={{ gridColumn: '1 / -1' }}>
              <Input label={t('einv.clientSecret')} type="password" value={form.clientSecret} onChange={set('clientSecret')} autoComplete="new-password" dir="ltr"
                placeholder={form.hasSecret ? `•••••••• ${t('einv.secretSaved')}` : ''} />
              <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 4 }}>🔒 {t('einv.secretNote')}</p>
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Btn type="button" variant="outline" onClick={test} disabled={testing}>{testing ? t('einv.testing') : `🔌 ${t('einv.test')}`}</Btn>
            {testResult && (
              <div style={{ flex: '1 1 260px', fontSize: 13, lineHeight: 1.6, color: testResult.ok ? 'var(--success)' : 'var(--danger)' }}>
                {testResult.lines.map((l, i) => <div key={i}>{i === 0 ? (testResult.ok ? '✓ ' : '✕ ') : ''}{l}</div>)}
              </div>
            )}
          </div>
        </Section>

        <Section title={t('einv.supplier')}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>{t('einv.supplierNote')}</p>
          <div style={grid}>
            <Input label={t('einv.tin')} value={form.tin || ''} onChange={set('tin')} maxLength={14} placeholder="C25845632020" dir="ltr" />
            <Select label={t('einv.idType')} value={form.idType || 'BRN'} onChange={set('idType')}>
              {ID_TYPES.map(x => <option key={x} value={x}>{t(`einv.id_${x}`)}</option>)}
            </Select>
            <Input label={t('einv.idValue')} value={form.idValue || ''} onChange={set('idValue')} maxLength={20} placeholder="202301012345" dir="ltr" />
            <Input label={t('einv.sstNo')} value={form.sstNo || ''} onChange={set('sstNo')} maxLength={35} dir="ltr" />
            <div>
              <Input label={t('einv.msic')} value={form.msicCode || ''} onChange={set('msicCode')} maxLength={5} inputMode="numeric" pattern="\d{5}" placeholder="62010" dir="ltr" />
              <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 4 }}>{t('einv.msicHelp')}</p>
            </div>
            <Input label={t('einv.activity')} value={form.businessActivity || ''} onChange={set('businessActivity')} maxLength={300} placeholder="Computer programming activities" />
            <Input label={t('einv.postcode')} value={form.postcode || ''} onChange={set('postcode')} maxLength={10} dir="ltr" />
            <Select label={t('einv.state')} value={form.stateCode || ''} onChange={set('stateCode')}>
              <option value="">{t('einv.chooseState')}</option>
              {codes.states.filter(s => s.code !== '17').map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
            </Select>
            <Input label={t('einv.phone')} value={form.phone || ''} onChange={set('phone')} maxLength={20} placeholder="03-1234 5678" dir="ltr" />
            <Input label={t('einv.ttxNo')} value={form.ttxNo || ''} onChange={set('ttxNo')} maxLength={17} dir="ltr" />
          </div>
        </Section>

        <Section title={t('einv.defaults')}>
          <div style={grid}>
            <div>
              <Select label={t('einv.classification')} value={form.classificationCode || '022'} onChange={set('classificationCode')}>
                {codes.classifications.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </Select>
              <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 4 }}>{t('einv.classificationHelp')}</p>
            </div>
            <Select label={t('einv.taxType')} value={form.taxType || ''} onChange={set('taxType')}>
              <option value="">{t('einv.taxNone')}</option>
              <option value="01">{t('einv.tax_01')}</option>
              <option value="02">{t('einv.tax_02')}</option>
            </Select>
          </div>
        </Section>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Btn type="submit" variant="primary" size="lg" disabled={saving}>{saving ? t('common.saving') : `💾 ${t('einv.save')}`}</Btn>
        </div>
      </div>
    </form>
  );
}
